import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';
import { formatPacificDateTime } from '@/lib/adminPacificTime';

const RANGE_OPTIONS = [
  { days: '7', label: '7d' },
  { days: '14', label: '14d' },
  { days: '30', label: '30d' },
  { days: '90', label: '90d' },
] as const;

type RangeDays = (typeof RANGE_OPTIONS)[number]['days'];
const ALLOWED_DAYS = new Set<string>(RANGE_OPTIONS.map((o) => o.days));

interface VariantMetrics {
  variant: 'control' | 'motion';
  visitors: number;
  store_taps: number;
  tap_rate: number | null;
  signups: number;
  payers: number;
}

interface DailyPoint {
  date: string;
  control_visitors: number;
  motion_visitors: number;
  control_taps: number;
  motion_taps: number;
}

interface ExperimentConfig {
  key: string;
  mode: 'off' | 'control' | 'split' | 'motion';
  enabled: boolean;
  motion_percent: number;
  cookie_name: string;
  assignment: string;
}

interface LandingExperimentResponse {
  experiment: ExperimentConfig;
  range: { days: number; start: string; end: string };
  generated_at: string;
  error: string | null;
  stale_at: string | null;
  variants: VariantMetrics[];
  lift: { absolute: number | null; relative: number | null };
  daily: DailyPoint[];
  notes: string[];
}

function formatNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString();
}

function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function formatLift(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const pct = value * 100;
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

function formatLiftPoints(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const points = value * 100;
  return `${points >= 0 ? '+' : ''}${points.toFixed(1)}pp`;
}

function modeLabel(config: ExperimentConfig): string {
  if (config.mode === 'split') {
    return `Live · ${config.motion_percent}% motion`;
  }
  if (config.mode === 'motion') return 'Forced motion';
  if (config.mode === 'control') return 'Paused · control only';
  return 'Off · control only';
}

function modeClasses(config: ExperimentConfig): string {
  if (config.mode === 'split') {
    return 'border-emerald-300 bg-emerald-50 text-emerald-900';
  }
  if (config.mode === 'motion') {
    return 'border-sky-300 bg-sky-50 text-sky-900';
  }
  return 'border-amber-300 bg-amber-50 text-amber-900';
}

function StatCard({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: 'control' | 'motion' | 'lift';
}) {
  const accentClass =
    accent === 'control'
      ? 'text-nova-primary'
      : accent === 'motion'
        ? 'text-orange-600'
        : accent === 'lift'
          ? 'text-emerald-700'
          : 'text-nova-foreground';
  return (
    <div className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm'>
      <p className='text-xs font-medium uppercase tracking-wide text-nova-muted-foreground'>
        {label}
      </p>
      <p className={`mt-1 text-2xl font-semibold ${accentClass}`}>{value}</p>
      {hint ? (
        <p className='mt-1 text-xs text-nova-muted-foreground'>{hint}</p>
      ) : null}
    </div>
  );
}

function VariantBadge({ variant }: { variant: 'control' | 'motion' }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
        variant === 'control'
          ? 'bg-sky-100 text-sky-800'
          : 'bg-orange-100 text-orange-800'
      }`}
    >
      {variant === 'control' ? 'Control' : 'Motion'}
    </span>
  );
}

function AdminLandingExperimentPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate({ from: '/admin/landing-experiment' });
  const { days } = Route.useSearch();
  const daysNum = Number(days);

  useEffect(() => {
    track('Landing Experiment Admin Screen', { days: daysNum });
  }, [daysNum]);

  const query = useQuery({
    queryKey: ['admin', 'landing-experiment', daysNum],
    queryFn: () =>
      apiJson<LandingExperimentResponse>(
        getToken,
        `/v1/admin/experiments/landing?days=${daysNum}`
      ),
    staleTime: 30_000,
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });

  const data = query.data;
  const control = data?.variants.find((v) => v.variant === 'control');
  const motion = data?.variants.find((v) => v.variant === 'motion');
  const maxDailyVisitors = Math.max(
    1,
    ...(data?.daily.map((d) =>
      Math.max(d.control_visitors, d.motion_visitors)
    ) ?? [1])
  );

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between'>
        <div>
          <h1 className='text-2xl font-semibold text-nova-foreground'>
            Landing A/B
          </h1>
          <p className='mt-1 text-sm text-nova-muted-foreground'>
            Control vs motion rendered at `/` for the `lp_motion_v1`
            experiment. Assignment is sticky and server-side; this page
            refreshes every 30 seconds.
          </p>
        </div>
        <div className='flex flex-wrap items-center gap-3'>
          <label className='text-sm text-nova-muted-foreground'>
            Range
            <select
              className='ml-2 rounded-md border border-nova-input bg-nova-card px-2 py-1 text-sm'
              value={days}
              onChange={(event) => {
                const value = event.target.value as RangeDays;
                void navigate({ search: { days: value } });
              }}
            >
              {RANGE_OPTIONS.map((option) => (
                <option key={option.days} value={option.days}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type='button'
            className='rounded-md border border-nova-border bg-nova-card px-3 py-1.5 text-sm font-medium text-nova-foreground hover:bg-nova-muted'
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
          >
            {query.isFetching ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {query.isLoading && !data ? (
        <div className='flex justify-center py-16'>
          <div className='h-8 w-8 animate-spin rounded-full border-2 border-nova-primary border-t-transparent' />
        </div>
      ) : null}

      {query.isError ? (
        <div className='rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800'>
          Failed to load the landing experiment.
        </div>
      ) : null}

      {data ? (
        <>
          <div
            className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${modeClasses(
              data.experiment
            )}`}
          >
            <div>
              <span className='font-semibold'>
                {modeLabel(data.experiment)}
              </span>
              <span className='ml-2 opacity-80'>
                {data.experiment.key} · cookie {data.experiment.cookie_name} ·
                server assignment
              </span>
            </div>
            {data.experiment.mode === 'off' ||
            data.experiment.mode === 'control' ? (
              <span className='text-xs opacity-80'>
                Existing motion cookies are rewritten to control on the next
                document load.
              </span>
            ) : null}
          </div>

          {data.error ? (
            <div className='rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900'>
              {data.stale_at
                ? `Showing cached data from ${formatPacificDateTime(
                    data.stale_at
                  )}. ${data.error}`
                : `Amplitude is unavailable: ${data.error}`}
            </div>
          ) : null}

          <div className='grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5'>
            <StatCard
              label='Control tap rate'
              value={formatPercent(control?.tap_rate)}
              hint={`${formatNumber(control?.store_taps)} taps / ${formatNumber(
                control?.visitors
              )} visitors`}
              accent='control'
            />
            <StatCard
              label='Motion tap rate'
              value={formatPercent(motion?.tap_rate)}
              hint={`${formatNumber(motion?.store_taps)} taps / ${formatNumber(
                motion?.visitors
              )} visitors`}
              accent='motion'
            />
            <StatCard
              label='Tap-rate lift'
              value={formatLift(data.lift.relative)}
              hint={`Absolute ${formatLiftPoints(data.lift.absolute)}`}
              accent='lift'
            />
            <StatCard
              label='Attributed signups'
              value={`${formatNumber(control?.signups)} / ${formatNumber(
                motion?.signups
              )}`}
              hint='Control / motion'
            />
            <StatCard
              label='Payers'
              value={`${formatNumber(control?.payers)} / ${formatNumber(
                motion?.payers
              )}`}
              hint='Control / motion'
            />
          </div>

          <section className='space-y-3 rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm'>
            <div className='flex flex-wrap items-baseline justify-between gap-2'>
              <h2 className='text-lg font-semibold text-nova-foreground'>
                Variants
              </h2>
              <span className='text-xs text-nova-muted-foreground'>
                {data.range.start} → {data.range.end} · Pacific days · updated{' '}
                {formatPacificDateTime(data.generated_at)}
              </span>
            </div>
            <div className='overflow-x-auto'>
              <table className='w-full text-left text-sm'>
                <thead className='border-b border-nova-border text-xs uppercase tracking-wide text-nova-muted-foreground'>
                  <tr>
                    <th className='py-2 pr-4 font-medium'>Variant</th>
                    <th className='py-2 pr-4 font-medium'>Visitors</th>
                    <th className='py-2 pr-4 font-medium'>Store taps</th>
                    <th className='py-2 pr-4 font-medium'>Tap rate</th>
                    <th className='py-2 pr-4 font-medium'>Signups</th>
                    <th className='py-2 font-medium'>Payers</th>
                  </tr>
                </thead>
                <tbody>
                  {data.variants.map((row) => (
                    <tr key={row.variant} className='border-b border-nova-border/60'>
                      <td className='py-2 pr-4'>
                        <VariantBadge variant={row.variant} />
                      </td>
                      <td className='py-2 pr-4'>
                        {formatNumber(row.visitors)}
                      </td>
                      <td className='py-2 pr-4'>
                        {formatNumber(row.store_taps)}
                      </td>
                      <td className='py-2 pr-4'>
                        {formatPercent(row.tap_rate)}
                      </td>
                      <td className='py-2 pr-4'>{formatNumber(row.signups)}</td>
                      <td className='py-2'>{formatNumber(row.payers)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className='space-y-3 rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm'>
            <div className='flex flex-wrap items-baseline justify-between gap-2'>
              <h2 className='text-lg font-semibold text-nova-foreground'>
                Daily
              </h2>
              <span className='text-xs text-nova-muted-foreground'>
                Bars show visitors; tap counts are daily unique tappers
              </span>
            </div>
            <div className='max-h-96 overflow-y-auto'>
              <table className='w-full text-left text-sm'>
                <thead className='sticky top-0 border-b border-nova-border bg-nova-card text-xs uppercase tracking-wide text-nova-muted-foreground'>
                  <tr>
                    <th className='py-2 pr-4 font-medium'>Date</th>
                    <th className='py-2 pr-4 font-medium'>Visitors</th>
                    <th className='py-2 pr-4 font-medium'>Control taps</th>
                    <th className='py-2 font-medium'>Motion taps</th>
                  </tr>
                </thead>
                <tbody>
                  {data.daily.map((point) => (
                    <tr
                      key={point.date}
                      className='border-b border-nova-border/60'
                    >
                      <td className='py-2 pr-4 whitespace-nowrap'>
                        {point.date}
                      </td>
                      <td className='py-2 pr-4'>
                        <div className='space-y-1'>
                          <div className='flex items-center gap-2'>
                            <div className='h-1.5 w-32 overflow-hidden rounded bg-nova-muted'>
                              <div
                                className='h-full bg-sky-500'
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (point.control_visitors / maxDailyVisitors) *
                                      100
                                  )}%`,
                                }}
                              />
                            </div>
                            <span className='text-xs text-nova-muted-foreground'>
                              C {formatNumber(point.control_visitors)}
                            </span>
                          </div>
                          <div className='flex items-center gap-2'>
                            <div className='h-1.5 w-32 overflow-hidden rounded bg-nova-muted'>
                              <div
                                className='h-full bg-orange-500'
                                style={{
                                  width: `${Math.min(
                                    100,
                                    (point.motion_visitors / maxDailyVisitors) *
                                      100
                                  )}%`,
                                }}
                              />
                            </div>
                            <span className='text-xs text-nova-muted-foreground'>
                              M {formatNumber(point.motion_visitors)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className='py-2 pr-4'>
                        {formatNumber(point.control_taps)}
                      </td>
                      <td className='py-2'>{formatNumber(point.motion_taps)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className='space-y-1 text-xs text-nova-muted-foreground'>
            {data.notes.map((note) => (
              <p key={note}>• {note}</p>
            ))}
            <p>
              • Treat small differences as noise: this test needs a large
              effect to be readable at current traffic.
            </p>
          </div>
        </>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/landing-experiment')({
  component: AdminLandingExperimentPage,
  validateSearch: (search: Record<string, unknown>) => {
    const daysRaw = typeof search.days === 'string' ? search.days : '30';
    return {
      days: (ALLOWED_DAYS.has(daysRaw) ? daysRaw : '30') as RangeDays,
    };
  },
});
